"use client";

import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import dynamic from "next/dynamic";

// German clone of mobile-proxies/product-highlights — same interactive
// visuals (globe, speed canvas), translated copy.

const GlobeComponent = dynamic(() => import("@/components/sections/shared/globe"), {
  ssr: false,
  loading: () => (
    <div className="bg-foreground/[0.03] aspect-square w-full animate-pulse rounded-full" />
  ),
});
const SpeedCanvas = dynamic(
  () => import("@/components/sections/products/mobile-proxies/product-highlights/speed-canvas"),
  {
    ssr: false,
    loading: () => (
      <div className="border-border bg-foreground/[0.03] aspect-[4/3] w-full animate-pulse rounded-2xl border" />
    ),
  }
);

export default function MobileProductHighlightsDe() {
  const prefersReducedMotion = useReducedMotion();
  const [speed, setSpeed] = useState(45);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      setSpeed(Math.floor(Math.random() * 90) + 10);
    }, 2800);
    return () => clearInterval(id);
  }, [prefersReducedMotion]);

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: inViewScrollRootMargin },
          transition: { duration: 0.5, delay },
        };

  return (
    <>
      <section
        id="worldwide-coverage-section"
        className="full-bleed band-wash py-section-sm border-border bg-band relative isolate overflow-hidden border-b"
        aria-labelledby="worldwide-coverage-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="relative order-2 min-w-0 lg:order-1">
              <GlobeComponent ariaLabel="Interaktiver 3D-Globus zur weltweiten Mobile-Proxy-Abdeckung" />
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="order-1 lg:order-2">
              <SectionHeader
                eyebrow="Abdeckung"
                id="worldwide-coverage-heading"
                heading="Mobile Proxies weltweit"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Proxidize betreibt ein verwaltetes, globales Mobile-Proxy-Netzwerk an Tausenden
                Standorten und gibt Kunden direkten Zugriff auf zuverlässige, Premium-Mobile-IPs —
                ohne eigene Hardware aufzubauen, zu hosten oder zu verwalten.
              </p>
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Das bedeutet direkte Kontrolle über die IP-Qualität an jedem Standort, ohne
                Drittanbieter-Infrastruktur zwischen Kunde und Netzwerk.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="speed-section"
        className="full-bleed band-wash py-section-sm border-border bg-band relative isolate overflow-hidden border-b"
        aria-labelledby="speed-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="order-2 lg:order-1">
              <SpeedCanvas speed={speed} />
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="order-1 lg:order-2">
              <SectionHeader
                eyebrow="Leistung"
                id="speed-heading"
                heading="Beruhigend schnell"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                Mit Geschwindigkeiten von durchschnittlich{" "}
                <span className="text-foreground font-semibold">10–50 Mbps</span>,
                standortübergreifend, mit Spitzenwerten bis zu{" "}
                <span className="text-foreground font-semibold">100 Mbps.</span>
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Für spezifische Geschwindigkeitsanforderungen ist ein individuelles SLA auf Anfrage
                verfügbar.
              </p>

              <div className="mb-8 grid grid-cols-2 gap-3">
                {[
                  { tier: "Standard", speed: "10–50 Mbps", desc: "Verlässliche Grundlage" },
                  { tier: "Burst", speed: "50–100 Mbps", desc: "Für datenintensive Aufgaben" },
                ].map((item) => (
                  <div
                    key={item.tier}
                    className="border-border/60 bg-foreground/[0.03] rounded-xl border p-3"
                  >
                    <p className="text-muted-foreground mb-0.5 text-[10px] font-bold tracking-wider uppercase">
                      {item.tier}
                    </p>
                    <p className="text-foreground/90 text-sm font-bold">{item.speed}</p>
                    <p className="text-muted-foreground mt-0.5 text-[11px]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
